import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { useCustomer } from "@/lib/customer";
import Logo from "@/components/Logo";
import { CustomerProfileCard } from "@/components/CustomerProfileCard";
import { MyDiscounts } from "@/components/MyDiscounts";

const PRIZE_STATUS = {
  redeemed: { label: "Ritirato", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  expired: { label: "Scaduto", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  pending: { label: "Da ritirare", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};
const prizeStatus = (p) => {
  if (p.redeemed) return "redeemed";
  return p.expired ? "expired" : "pending";
};

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function MyPrizes() {
  const navigate = useNavigate();
  const { customer } = useCustomer();
  const [plays, setPlays] = useState(null);
  useEffect(() => {
    if (customer === null) navigate("/accedi?next=/i-miei-premi", { replace: true });
    if (customer) api.customerPlays().then(setPlays).catch(() => setPlays([]));
  }, [customer, navigate]);
  const wins = (plays || []).filter((p) => p.result === "win");
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button data-testid="my-prizes-back" variant="ghost" size="icon" onClick={() => navigate("/concorsi")}><ArrowLeft className="h-5 w-5" /></Button>
          <Logo className="h-10" />
          <div><h1 className="font-display font-bold text-xl">I miei premi</h1><p className="text-xs text-muted-foreground">{customer?.name || customer?.email}</p></div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-8 space-y-3" data-testid="my-prizes-page">
        {customer && <CustomerProfileCard key={customer.id} />}
        {customer && <MyDiscounts />}
        {!plays ? <p className="text-muted-foreground">Caricamento…</p> : (
          <>
            <p className="text-sm text-muted-foreground">{plays.length} giocate · {wins.length} vincite</p>
            {wins.length === 0 && <Card className="p-10 text-center border-dashed border-2 bg-card/50"><Trophy className="h-10 w-10 mx-auto text-muted-foreground/40" /><p className="text-muted-foreground mt-3">Nessuna vincita ancora. In bocca al lupo!</p></Card>}
            {wins.map((p) => (
              <Card key={p.id} className="p-4 bg-card border-border" data-testid={`my-prize-${p.id}`}>
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-amber-400" />
                  <h3 className="font-semibold flex-1">{p.prize_name}</h3>
                  <Badge className={`border ${PRIZE_STATUS[prizeStatus(p)].cls}`} data-testid={`my-prize-status-${p.id}`}>
                    {PRIZE_STATUS[prizeStatus(p)].label}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{p.contest_name} · {fmt(p.created_at)}{p.store_name ? ` · ${p.store_name}` : ""}</p>
                <p className="text-xs mt-2">Seriale: <span className="font-mono-code font-semibold">{p.receipt_serial}</span>{p.expires_at && !p.redeemed ? ` · scade il ${fmt(p.expires_at)}` : ""}</p>
                <a href={api.receiptPdfUrl(p.id, p.receipt_token)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs underline mt-2 text-primary" data-testid={`my-prize-pdf-${p.id}`}><FileDown className="h-3.5 w-3.5" /> Ricevuta PDF</a>
              </Card>
            ))}
          </>
        )}
      </main>
    </div>
  );
}
